<template>
    <div
        class="calc-stepper"
        role="navigation"
        :aria-label="$t('§calc.stepper_aria.progress')"
    >
        <template v-for="(label, i) in items" :key="label">
            <button
                type="button"
                class="calc-stepper__item"
                :disabled="!isReachable(i + 1)"
                :aria-current="(i + 1) === current ? 'step' : undefined"
                :title="titleFor(i + 1, label)"
                @click="onClick(i + 1)"
            >
                <div
                    class="calc-stepper__bubble"
                    :class="`calc-stepper__bubble--${stateOf(i + 1)}`"
                >
                    <CalcIcon
                        v-if="stateOf(i + 1) === 'done'"
                        name="check"
                        color="rgb(26,161,118)"
                        :size="10"
                    />
                    <template v-else>{{ i + 1 }}</template>
                </div>
                <span
                    class="calc-stepper__label"
                    :class="`calc-stepper__label--${stateOf(i + 1)}`"
                >
                    {{ $t(label) }}
                </span>
            </button>
            <span
                v-if="i < items.length - 1"
                class="calc-stepper__line"
                aria-hidden="true"
            />
        </template>
    </div>
</template>

<script>
import CalcIcon from './CalcIcon.vue'

export default {
    name: 'CalcStepper',

    components: { CalcIcon },

    props: {
        currentStep: {
            type: Number,
            required: true,
        },
        maxReached: {
            type: Number,
            default: null,
        },
    },

    emits: ['step-click'],

    computed: {
        items() {
            return [
                '§calc.stepper.financing',
                '§calc.stepper.contract',
                '§calc.stepper.preview',
            ]
        },
        current() {
            return this.currentStep
        },
    },

    methods: {
        stateOf(idx) {
            if (idx < this.current) return 'done'
            if (idx === this.current) return 'current'
            return 'upcoming'
        },
        isReachable(idx) {
            if (this.maxReached === null) return true
            return idx <= this.maxReached
        },
        titleFor(idx, labelKey) {
            const label = this.$t(labelKey)
            const key = this.isReachable(idx)
                ? '§calc.stepper_aria.go_to'
                : '§calc.stepper_aria.locked'
            return this.$t(key, { label })
        },
        onClick(idx) {
            if (!this.isReachable(idx)) return
            if (idx === this.current) return
            this.$emit('step-click', idx)
        },
    },
}
</script>

<style scoped>
.calc-stepper {
    display: flex;
    align-items: flex-start;
    gap: 0;
    position: relative;
    top: 10px;
}

.calc-stepper__item {
    position: relative;
    width: 110px;
    height: 44px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    gap: 4px;
    background: transparent;
    cursor: pointer;
    flex: 0 0 auto;
    transition: opacity var(--calc-t-fast);
    padding: 0;
    border: none;
    color: inherit;
    font-family: inherit;
}

.calc-stepper__item:hover:not(:disabled) {
    opacity: 0.72;
}

.calc-stepper__item:disabled {
    cursor: default;
}

.calc-stepper__bubble {
    width: 20px;
    height: 20px;
    border-radius: 100px;
    display: grid;
    place-items: center;
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    font-size: 10px;
    line-height: 16px;
    text-align: center;
    transition:
        background var(--calc-t-med),
        color var(--calc-t-med);
    flex: 0 0 auto;
}

.calc-stepper__bubble--done {
    background: var(--calc-green-mint);
    color: var(--calc-green-deep);
}

.calc-stepper__bubble--current {
    background: var(--calc-ink);
    color: white;
}

.calc-stepper__bubble--upcoming {
    background: var(--calc-ink);
    color: white;
    opacity: 0.5;
}

.calc-stepper__label {
    font-family: 'TWK Lausanne', 'Inter', sans-serif;
    font-size: 12px;
    line-height: 20px;
    color: var(--calc-ink);
    white-space: nowrap;
    text-align: center;
    font-weight: 400;
}

.calc-stepper__label--done,
.calc-stepper__label--upcoming {
    opacity: 0.5;
}

.calc-stepper__line {
    flex: 0 0 auto;
    width: clamp(56px, 11vw, 130px);
    height: 1px;
    background: var(--calc-connector);
    opacity: 0.5;
    margin: 10px 6px 0;
}

@media (max-width: 760px) {
    .calc-stepper {
        justify-content: center;
    }
    .calc-stepper__item {
        width: auto;
        min-width: 0;
        padding: 0 4px;
    }
    .calc-stepper__label {
        display: none;
    }
    .calc-stepper__line {
        width: clamp(28px, 8vw, 80px);
        margin: 10px -4px 0;
    }
}
</style>
